// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PopupContextProvider from "@gcg/App/Components/XPopup/XContext";
import XPopup from "@gcg/App/Components/XPopup/XPopup";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setManageShareListModalIsOpen from "@insite/client-framework/Store/Components/ManageShareListModal/Handlers/SetManageShareListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import HelpCircle from "@insite/mobius/Icons/HelpCircle";
import Users from "@insite/mobius/Icons/Users";
import Link from "@insite/mobius/Link";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: WishListSharingStatusStyles;
    /**
     * @deprecated Use the `wishList.id` property instead.
     */
    isSharedList?: boolean;
    /**
     * @deprecated Use the `wishList.id` property instead.
     */
    wishListSharesCount?: number;
    /**
     * @deprecated Use the `wishList.id` property instead.
     */
    sharedByDisplayName?: string;
    wishList?: WishListModel;
    showNoPermissionsTooltip?: boolean;
}

const mapDispatchToProps = {
    setManageShareListModalIsOpen,
};

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
});

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface WishListSharingStatusStyles {
    statusText?: TypographyProps;
    privateIcon?: IconPresentationProps;
    sharedIcon?: IconPresentationProps;
    privateTooltip?: TooltipPresentationProps;
}

export const wishListSharingStatusStyles: WishListSharingStatusStyles = {
    privateIcon: {
        src: HelpCircle,
        size: 14,
        css: css`
            margin-left: 3px;
            margin-right: 3px;
        `,
    },

    sharedIcon: {
        src: Users,
        size: 14,
        css: css`
            margin-right: 3px;
        `,
    },
    statusText: {
        size: 14,
        css: css`
            display: flex;
        `,
    },
};

const WishListSharingStatus: FC<Props> = ({
    extendedStyles,
    isSharedList,
    wishListSharesCount,
    sharedByDisplayName,
    wishList,
    setManageShareListModalIsOpen,
    wishListSettings,
    showNoPermissionsTooltip,
}) => {
    const [styles] = React.useState(() => mergeToNew(wishListSharingStatusStyles, extendedStyles));

    if (!wishListSettings?.allowMultipleWishLists || !wishListSettings?.allowListSharing) {
        return null;
    }

    const manageShareListClickHandler = () => {
        setManageShareListModalIsOpen({ modalIsOpen: true, wishListId: wishList?.id });
    };

    const isSharedListInner = isSharedList || wishList?.isSharedList || false;
    const wishListSharesCountInner = wishListSharesCount || wishList?.wishListSharesCount || 0;
    const sharedByDisplayNameInner = sharedByDisplayName || wishList?.sharedByDisplayName;

    return (
        <>
            {!isSharedListInner && wishListSharesCountInner === 0 && (
                <Typography {...styles.statusText} data-test-selector="myLists_PrivateLabel">
                    {translate("Private")}
                    <PopupContextProvider>
                        <XPopup
                            type="tooltip"
                            trigger={
                                <StyledWrapper>
                                    <Icon {...styles.privateIcon} size={14} color="secondary" />
                                </StyledWrapper>
                            }
                            position="right"
                        >
                            <StyledWrapper className="popup-content-wrapper">
                                {siteMessage("Lists_Share_List_Tooltip") as string}
                            </StyledWrapper>
                        </XPopup>
                    </PopupContextProvider>
                </Typography>
            )}
            {!isSharedListInner && wishListSharesCountInner > 0 && (
                <>
                    <Link
                        {...styles.statusText}
                        css={css`
                            font-size: 14px;
                        `}
                        onClick={() => manageShareListClickHandler()}
                        data-test-selector="shareWithOthers"
                    >
                        <Icon {...styles.sharedIcon} />
                        {translate("Shared with")} {wishListSharesCountInner} {translate("others")}
                    </Link>
                </>
            )}
            {isSharedListInner && sharedByDisplayNameInner && (
                <Typography {...styles.statusText} data-test-selector="myLists_listSharedBy">
                    <Icon {...styles.sharedIcon} />
                    {translate("Shared by")} {sharedByDisplayNameInner}
                    {!wishList?.allowEdit && showNoPermissionsTooltip && (
                        <PopupContextProvider>
                            <XPopup
                                type="tooltip"
                                trigger={
                                    <StyledWrapper>
                                        <Icon {...styles.privateIcon} size={14} color="secondary" />
                                    </StyledWrapper>
                                }
                                position="right"
                            >
                                <StyledWrapper className="popup-content-wrapper">
                                    {siteMessage("Lists_Share_List_No_Permissions_Tooltip") as string}
                                </StyledWrapper>
                            </XPopup>
                        </PopupContextProvider>
                    )}
                </Typography>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(WishListSharingStatus);
